import React from 'react';
import { useTranslation } from 'react-i18next';

// ── Shared "email activity" badge (owner 2026-08-30) ────────────────────────────────────────────
// Extracted from Peak's local SentBadge, which only Quotation / Invoice / Receipt could use. The
// same ✉N marker is wanted wherever a record carries correspondence ("我们的系统有大量的地方都需要
// 显示发邮件的图标实际收发邮件的数量"), so it lives here and every list renders the identical thing.
//
// Two shapes, one component:
//   · count only            — how many times WE emailed the document (Peak's original meaning).
//   · count + inbound       — a whole conversation: total messages, of which `inbound` came back.
//     `unread` turns the badge amber and appends a dot, so a reply nobody has read stands out.
// Rendering nothing when there is no traffic is deliberate: an empty row must stay quiet.

const fmtEpoch = (s?: number) => {
  if (!s) return '';
  const d = new Date(s * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

export default function MailBadge({ count, last, inbound, unread, onClick, title }: {
  count?: number;          // total messages (or times emailed)
  last?: number;           // epoch seconds of the most recent one
  inbound?: number;        // how many of them came FROM the counterparty
  unread?: number;         // inbound messages nobody has opened yet
  onClick?: () => void;    // opens the thread viewer where the list has one
  title?: string;          // overrides the derived tooltip
}) {
  const { t } = useTranslation();
  if (!count || count < 1) return null;
  const hasUnread = !!unread && unread > 0;
  // Amber means "look at me": either an unread reply, or (Peak's original rule) a document we have
  // emailed more than once. Green is the settled, nothing-to-do state.
  const warn = hasUnread || (inbound === undefined && count >= 2);
  const tip = title
    || (inbound !== undefined
      ? `${count} message(s)${inbound ? ` · ${inbound} received` : ''}${hasUnread ? ` · ${unread} unread` : ''}${last ? ' · ' + fmtEpoch(last) : ''}`
      : (onClick ? t('iv_thread_open') : `${t('iv_sent_n').replace('{n}', String(count))}${last ? ' · ' + fmtEpoch(last) : ''}`));
  return (
    <span title={tip} onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11.5, fontWeight: 700,
        color: warn ? '#B45309' : '#1D9E75', background: warn ? '#FEF3C7' : '#E7F6EE',
        borderRadius: 999, padding: '2px 7px', marginRight: 8, verticalAlign: 'middle',
        cursor: onClick ? 'pointer' : 'default',
      }}>
      <i className="ti ti-mail" style={{ fontSize: 12 }} aria-hidden />{count}
      {hasUnread && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#DC2626', marginLeft: 1 }} aria-hidden />}
    </span>
  );
}
